import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useOutletContext, Navigate } from 'react-router-dom';
import { createSlug } from '../../utils/helpers';
import BookingFlow from '../components/BookingFlow';
import { traceStorefrontBooking } from '../storefront-booking/generateStorefrontTimeSlots';
import { fetchStorefrontServices } from '../storefront-booking/storefrontServicesCache';
import { getStorefrontBookingConfirmationPath } from '../storefront-booking/storefrontBookingPaths';
import { PageSkeleton } from '../../components/loading/skeletons';
import '../styles/LandingPage.css';
import '../styles/BookingPage.css';

const CustomerBookingPage = ({ prefetchedService = null }) => {
  const { companyUrl, serviceSlug, storefrontSlug } = useParams();
  const resolvedSlug = serviceSlug ?? storefrontSlug ?? '';
  const navigate = useNavigate();
  const { companyData } = useOutletContext();
  const [service, setService] = useState(prefetchedService || null);
  const [loading, setLoading] = useState(!prefetchedService);
  const [error, setError] = useState(null);

  useEffect(() => {
    traceStorefrontBooking('customerBookingPage.active', {
      companyId: companyData?.id,
      serviceSlug: resolvedSlug,
      flow: 'storefront-booking-flow',
      viewTrace: 'dumpStorefrontBookingTrace()',
    });
  }, [companyData?.id, resolvedSlug]);

  useEffect(() => {
    if (prefetchedService) {
      setService(prefetchedService);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setService(null);

        if (resolvedSlug) {
          const services = await fetchStorefrontServices(companyData.id);
          const serviceData = services.find((entry) => createSlug(entry.name) === resolvedSlug);

          if (serviceData) {
            setService(serviceData);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (companyData?.id) {
      fetchData();
    }
  }, [companyData?.id, resolvedSlug, prefetchedService]);

  const handleBookingComplete = ({ appointmentId, publicNumber, summary } = {}) => {
    if (appointmentId) {
      navigate(getStorefrontBookingConfirmationPath(companyUrl, appointmentId, publicNumber), {
        state: { summary: { ...summary, publicNumber } },
      });
      return;
    }

    navigate(`/book/${companyUrl}`);
  };

  const handleCancel = () => {
    // Navigate back to service details if we came from there, otherwise to landing page
    if (resolvedSlug) {
      navigate(`/book/${companyUrl}/${resolvedSlug}`);
    } else {
      navigate(`/book/${companyUrl}`);
    }
  };

  if (loading) {
    return (
      <div className="landing-page">
        <PageSkeleton padded variant="form" label="Loading booking" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="landing-page">
        <div className="error-container">
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
          <Link to={`/book/${companyUrl}`} className="back-link">
            Return to Services
          </Link>
        </div>
      </div>
    );
  }

  if (!service) {
    return <Navigate to={`/book/${companyUrl}`} replace />;
  }

  return (
    <div className="booking-page-container">
      <div className="container">
        <div className="booking-content">
          <BookingFlow
            companyData={companyData}
            selectedService={service}
            onBookingComplete={handleBookingComplete}
            onCancel={handleCancel}
          />
        </div>
      </div>
    </div>
  );
};

export default CustomerBookingPage;

